import { Checkbox } from "@components/ui/checkbox";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { IndividualQuery, QueryData } from "@utils/interface/index.interface";
import {
  Permission,
  PermissionGroupData,
  RoleData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

const RoleForm: React.FC = () => {
  const setIsFormPending = useSelectionStore((state) => state.setIsFormPending);
  const permissionsList = ["View", "Add", "Edit", "Delete"];
  const [selected, setSelected] = useState<number[]>([]);

  const queryClient = useQueryClient();
  const { handleAction, searchParams } = useHandleAction({});
  const pageItem = searchParams.get("page-item");
  // const pageMode = searchParams.get("page");
  const pathname = usePathname();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      permission_ids: selected || [],
    },
  });
  console.log("🚀 ~ errors:", errors);

  const { data, isPending } = useListData<QueryData<PermissionGroupData>>({
    endpoint: endpoint["permissions"],
    key: "permissionsList",
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<RoleData>
  >({
    endpoint: endpoint["groups"],
    id: pageItem || "",
    key: "groups" + pageItem,
    enabled: pageItem ? true : false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.data && searchParams.get("page") === "edit") {
      const permissionId = fetchData.data.permissions?.map(
        (item: any) => item.id
      );
      let defaultValues = {
        name: fetchData?.data.name,
        permission_ids: permissionId,
      };

      console.log("🚀 ~ RoleForm ~ defaultValues:", defaultValues);

      setSelected(() => permissionId);
      reset(defaultValues);
    }
  }, [fetchData, reset, searchParams, setIsRetrive]);

  const { mutate } = usePostData();

  const handleMutate = (data: any) => {
    setIsFormPending(true);

    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
    }
    return {
      data,
      endpoint: endpoint["groups"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Role ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Role",
      });
      handleAction({ type: "page-close" });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const onSubmit = (data: any) => {
    if (searchParams.get("item") && !data.id) {
      data.id = searchParams.get("item");
    }
    if (!data["name"]) return throwErrorAlert({ data: "Role must have name" });
    if (!selected.length)
      return throwErrorAlert({
        data: "Role must have at least one permission",
      });
    data["permission_ids"] = selected;
    setIsFormPending(true);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
    // mutate(
    //   {
    //     endpoint: endpoint["groups"],
    //     data: data,
    //     method: data.id ? METHODS.PUT : METHODS.POST,
    //   },
    //   {
    //     onSuccess: ({ status, data: updateData }) => {

    //     },
    //     onSettled: () => setIsFormPending(false),
    //     onError: () => setIsFormPending(false),
    //   }
    // );
  };

  const cleanData = (data = {}) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        Array.isArray(value) &&
          value?.filter(
            (item: Permission) =>
              item.action !== "Manage" && item.action !== "Can"
          ),
      ])
    );
  };

  const handleCheck = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelected((prev) =>
      checked ? [...prev, id] : prev?.filter((item) => item !== id)
    );
  };

  const handleSelectAll = (action: string, checked: boolean) => {
    let newSelected = new Set(selected);
    Object.values(cleanData(data?.data)).forEach((permissions) => {
      Array.isArray(permissions) &&
        permissions.forEach((perm: any) => {
          if (perm.action.toLowerCase() === action.toLowerCase()) {
            if (checked) {
              newSelected.add(perm.id);
            } else {
              newSelected.delete(perm.id);
            }
          }
        });
    });
    setSelected(Array.from(newSelected));
  };

  if (isPending)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <form id={pathname} onSubmit={handleSubmit(onSubmit)}>
      <div className="flex gap-4">
        <input
          {...register("name")}
          className="border-0 border-b-2 border-gray-500 w-full outline-none h-[44px] px-2"
          type="text"
          placeholder="Enter role name"
        />
      </div>
      <div className="mt-4 text-gray-500 text-sm">
        Give a suitable description for the Role
      </div>

      <div className="mt-6">
        <div className="text-lg font-semibold">
          Manage permissions for{" "}
          <span className="text-blue-500">{watch("name") || "Strategist"}</span>
        </div>
        <div>
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 ">Manage</th>

                {permissionsList.map((action) => (
                  <th key={action} className="p-2 border text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-semibold capitalize">
                        {action} (Select All)
                      </span>
                      <Checkbox
                        checked={Object.values(cleanData(data?.data)).every(
                          (permissions) =>
                            Array.isArray(permissions) &&
                            permissions
                              ?.filter(
                                (perm: Permission) =>
                                  perm.action.toLowerCase() ===
                                  action.toLowerCase()
                              )
                              ?.every((perm: Permission) =>
                                selected.includes(perm.id)
                              )
                        )}
                        onCheckedChange={(checked: boolean) =>
                          handleSelectAll(action, checked)
                        }
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(cleanData(data?.data))
                ?.filter(([key]) =>
                  searchParams.get("filter")
                    ? key
                        .toLowerCase()
                        .includes(
                          searchParams.get("filter")?.toLowerCase() || ""
                        )
                    : true
                )
                .map(([key, items]) => (
                  <tr key={key} className="p-4 border">
                    <td className="p-2 capitalize">{key}</td>
                    {permissionsList.map((action) => {
                      const item =
                        Array.isArray(items) &&
                        items.find(
                          (perm: Permission) =>
                            perm.action.toLowerCase() === action.toLowerCase()
                        );
                      return (
                        <td key={action} className="text-center">
                          {item && (
                            <Checkbox
                              checked={selected.includes(item.id)}
                              onCheckedChange={(checked: boolean) =>
                                handleCheck({ id: item.id, checked })
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* )} */}
    </form>
  );
};

export default RoleForm;
